<template>
  <header
    id="pageHeader"
    class="header"
  >
    <div
      class="header__left header__item"
      :class="{ 'learn-tooltip pointer-none': selectedStep === step }"
    >
      <router-link
        to="/"
        class="logo header__logo"
      >
        <img
          :src="
            require(
              `@/assets/images/${isStagingFlag ? 'staging-logo' : 'logo'}${isDark ? '-dark' : ''}.svg`
            )
          "
          alt=""
        />
      </router-link>
      <BaseHeading
        level="2"
        className="header__title"
      >
        {{ mainHeaderTitle }}
      </BaseHeading>
      <div id="header-left-actions" />

      <Breadcrumbs />
    </div>
    <div class="header__right header__item">
      <div id="header-right" />
      <router-link
        v-if="calendarRoute"
        v-tooltip.bottom="{ content: 'Calendar', theme: 'info-tooltip' }"
        :to="calendarRoute"
        class="common-gray-interactive header__icon-link"
        aria-label="Calendar"
        tabindex="-1"
      >
        <icon
          class="img-icon-fill"
          data="@icon/calendar-full.svg"
          :fill="true"
          width="var(--icon-24)"
          height="var(--icon-24)"
          color="var(--monochrome05to04)"
        />
      </router-link>
      <HeaderTimesheetMenu v-if="timesheetMenuEnabled && account?.timesheets" />

      <NotificationList />

      <router-link
        v-if="account?.urlName"
        v-tooltip.bottom="{ content: 'My Work', theme: 'info-tooltip' }"
        :to="{
          name: 'myWork',
          params: { accountId: account.urlName }
        }"
        class="common-gray-interactive header__icon-link"
        aria-label="My Work"
        tabindex="-1"
      >
        <icon
          class="img-icon-fill"
          data="@icon/my-work-checkbox.svg"
          :fill="true"
          width="var(--icon-24)"
          height="var(--icon-24)"
          color="var(--monochrome05to04)"
        />
      </router-link>

      <HeaderMenu :key="$route.path" />
      <HeaderProfileMenu />
    </div>
    <Teleport to="body">
      <ModalAiSetup />
    </Teleport>
  </header>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { useAcl } from "vue-simple-acl"
import { useFlag } from "@unleash/proxy-client-vue"
import { Getter } from "s-vuex-class"

import HeaderMenu from "@/components/Header/HeaderMenu.vue"
import HeaderProfileMenu from "@/components/Header/HeaderProfileMenu.vue"
import HeaderTimesheetMenu from "@/components/Header/HeaderTimesheetMenu.vue"
import NotificationList from "@/components/Notification/NotificationList.vue"

import { isStaging } from "@/app.config"
import { Account, TipsKeyType, TipsStepType, User } from "@/models"
import { UnleashFlags } from "@/plugins/unleash"
import { Getters } from "@/store"

import ModalAiSetup from "../Modal/ModalAiSetup.vue"
import Breadcrumbs from "./Breadcrumbs.vue"

@Options({
  name: "Header",
  components: {
    HeaderTimesheetMenu,
    HeaderProfileMenu,
    HeaderMenu,
    NotificationList,
    Breadcrumbs,
    ModalAiSetup
  }
})
export default class Header extends Vue {
  @Getter(Getters.GET_USER_CURRENT_PROFILE) user: User
  @Getter(Getters.GET_ACCOUNT) readonly account: Account
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean
  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType

  timesheetMenuEnabled = useFlag(UnleashFlags.TIME_TRACKING)

  step: TipsStepType = "breadcrumbs"

  get accountName() {
    return this.account?.name ?? ""
  }

  get mainHeaderTitle() {
    return this.$route.meta?.adminPage &&
      (this.isSuperAdmin || this.$route.meta?.showAdminPanelTitle)
      ? "Admin Panel"
      : this.accountName
  }

  get calendarRoute() {
    const { accountId } = this.$route.params

    if (!accountId) {
      return null
    }

    return {
      name: "team-calendar",
      params: {
        accountId
      }
    }
  }

  get isStagingFlag() {
    return isStaging()
  }

  get isSuperAdmin() {
    return useAcl().can("superAdmin")
  }
}
</script>
